import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { TenantProvider } from "@/context/TenantContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import Login from "@/pages/Login";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import GenericPage from "@/pages/GenericPage";
import Jobs from "@/pages/Jobs";
import Customers from "@/pages/Customers";
import Sites from "@/pages/Sites";
import ContractMargins from "@/pages/ContractMargins";
import People from "@/pages/People";
import Projects from "@/pages/Projects";
import Schedule from "@/pages/Schedule";
import JobTemplates from "@/pages/JobTemplates";
import Unscheduled from "@/pages/Unscheduled";
import Billing from "@/pages/Billing";
import Settings from "@/pages/Settings";
import Reports from "@/pages/Reports";
import Audit from "@/pages/Audit";
import Technician from "@/pages/Technician";
import AdminDashboard from "@/pages/superadmin/AdminDashboard";
import Companies from "@/pages/superadmin/Companies";
import CompanyDetail from "@/pages/superadmin/CompanyDetail";

function Splash() {
  return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined) return <Splash />;
  if (user === null) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (roles && !roles.includes(user.role)) {
    const dest = user.role === "amden_super_admin" ? "/admin" : user.role === "field_technician" ? "/technician" : "/app/dashboard";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined) return <Splash />;
  if (user === null) return <Navigate to="/login" replace />;
  const dest = user.role === "amden_super_admin" ? "/admin" : user.role === "field_technician" ? "/technician" : "/app/dashboard";
  return <Navigate to={dest} replace />;
}

function Workspace() {
  return (
    <TenantProvider>
      <AppShell />
    </TenantProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/login/:slug" element={<Login />} />
          <Route path="/" element={<RootRedirect />} />

          <Route path="/admin" element={<Protected roles={["amden_super_admin"]}><AdminDashboard /></Protected>} />
          <Route path="/admin/companies" element={<Protected roles={["amden_super_admin"]}><Companies /></Protected>} />
          <Route path="/admin/companies/:id" element={<Protected roles={["amden_super_admin"]}><CompanyDetail /></Protected>} />

          <Route path="/technician" element={<Protected><TenantProvider><Technician /></TenantProvider></Protected>} />

          <Route path="/app" element={<Protected><Workspace /></Protected>}>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="customers" element={<Customers />} />
            <Route path="sites" element={<Sites />} />
            <Route path="contracts" element={<ContractMargins />} />
            <Route path="employees" element={<People />} />
            <Route path="projects" element={<Projects />} />
            <Route path="job-templates" element={<JobTemplates />} />
            <Route path="unscheduled" element={<Unscheduled />} />
            <Route path="schedule" element={<Schedule />} />
            <Route path="billing" element={<Billing />} />
            <Route path="reports" element={<Reports />} />
            <Route path="audit" element={<Audit />} />
            <Route path="settings" element={<Settings />} />
            <Route path=":entity" element={<GenericPage />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
